import * as React from 'react';

import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  DragOverEvent,
  useSensor,
  useSensors,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
} from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove } from '@dnd-kit/sortable';
import { useTranslations } from '@rematter/modules/transportation/i18n/useTranslations';
import { TaskCard, Task as KanbanTask, Label, Loading } from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';
import { useAtomValue, useSetAtom } from 'jotai';
import { createPortal } from 'react-dom';

import { $getLocalServices } from '../../state/loadBoard.localState';
import { $routeForm, $updateRouteForm } from '../../state/routeForm.localState';
import { ServiceRecord } from '../../types';

import { RouteMapTab } from './RouteMapTab';
import { ServiceContent } from './ServiceContent';

const sortByRoutePosition = (a: ServiceRecord, b: ServiceRecord) => {
  // If both services are in the route, sort by routePosition
  if (a.routePosition !== undefined && b.routePosition !== undefined) {
    return a.routePosition - b.routePosition;
  }

  // If only one service is in the route, prioritize it
  if (a.routePosition !== undefined) return -1;
  if (b.routePosition !== undefined) return 1;

  // If neither service is in the route, maintain original order
  return 0;
};

interface ServiceListBoxProps {
  title: string;
  placeholders: React.ReactNode[];
  isLoading?: boolean;
  services: KanbanTask[];
  columnId: string;
  isViewMode?: boolean;
}

const ServiceListBox = ({
  title,
  placeholders,
  isLoading,
  services,
  columnId,
  isViewMode,
}: ServiceListBoxProps & { isViewMode?: boolean }) => {
  const serviceIds = React.useMemo(
    () => services.map((service) => service.id),
    [services],
  );

  const { setNodeRef, isDragging } = useSortable({
    id: columnId,
    data: {
      type: 'Column',
      columnId,
    },
    disabled: isViewMode,
  });

  return (
    <div className="@ServiceListBox flex flex-1 flex-col gap-2">
      <Label>{title}</Label>
      <div
        ref={setNodeRef}
        className={cn(
          'bg-background flex max-h-[312px] min-h-0 flex-1 flex-col rounded-md border border-neutral/20 p-2',
          isDragging ? 'ring-2 ring-primary' : '',
        )}
      >
        <Loading isLoading={isLoading}>
          {serviceIds.length > 0 ? (
            <div
              className={cn(
                'no-scrollbar flex flex-1 flex-col items-center overflow-y-auto',
                isViewMode && 'pointer-events-none opacity-60',
              )}
            >
              <SortableContext items={serviceIds}>
                {services.map((service) => (
                  <TaskCard
                    key={service.id}
                    task={service}
                    className="mb-2 flex-shrink-0"
                  />
                ))}
              </SortableContext>
            </div>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-start text-foreground/40">
              {placeholders.map((ph, i) => (
                <span
                  key={i}
                  className={
                    i === 0
                      ? 'mb-1 text-base text-neutral/50'
                      : 'text-xs text-neutral/50'
                  }
                >
                  {ph}
                </span>
              ))}
            </div>
          )}
        </Loading>
      </div>
    </div>
  );
};

export type RouteServiceSelectorProps = {
  isViewMode?: boolean;
  activeTab?: 'services' | 'routeMap';
};

export const RouteServiceSelector = ({
  isViewMode = false,
  activeTab = 'services',
}: RouteServiceSelectorProps) => {
  const t = useTranslations();
  const form = useAtomValue($routeForm);
  const updateForm = useSetAtom($updateRouteForm);
  const [availableServices, setAvailableServices] = React.useState<
    KanbanTask[]
  >([]);
  const [activeService, setActiveService] = React.useState<KanbanTask | null>(
    null,
  );
  const localServices = useAtomValue($getLocalServices);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        delay: 120,
        tolerance: 30,
      },
    }),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor),
  );

  const createKanbanService = React.useCallback(
    (service: ServiceRecord): KanbanTask => ({
      id: service.id,
      columnId: service.routeId ?? 'unassigned',
      rank: service.rank,
      content: () => <ServiceContent service={service} />,
    }),
    [],
  );

  React.useEffect(() => {
    const routeServices = form.id
      ? localServices.filter((service) => service.routeId === form.id)
      : [];

    const availableKanbanServices = localServices
      .filter((service) => service.routeId === undefined)
      .sort(sortByRoutePosition);

    const allServices = [
      ...(isViewMode ? [] : availableKanbanServices),
      ...routeServices,
    ]
      .sort(sortByRoutePosition)
      .map(createKanbanService);

    setAvailableServices(allServices);
  }, [localServices, createKanbanService, form.id, isViewMode]);

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const service = availableServices.find((s) => s.id === active.id);
    if (service) {
      setActiveService(service);
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;

    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    if (activeId === overId) return;

    const activeService = availableServices.find((s) => s.id === activeId);
    if (!activeService) return;

    // Check if we're dragging over a service
    const overService = availableServices.find((s) => s.id === overId);
    if (overService) {
      setTimeout(() => {
        setAvailableServices((services) => {
          const activeIndex = services.findIndex((s) => s.id === activeId);
          const overIndex = services.findIndex((s) => s.id === overId);
          const activeService = services[activeIndex];
          const overService = services[overIndex];

          if (
            activeService &&
            overService &&
            activeService.columnId !== overService.columnId
          ) {
            // Moving between columns
            const newServices = services.map((service) =>
              service.id === activeId
                ? { ...service, columnId: overService.columnId }
                : service,
            );
            return arrayMove(
              newServices,
              activeIndex,
              Math.max(overIndex - 1, 0),
            );
          }

          // Reordering within the same column
          return arrayMove(services, activeIndex, overIndex);
        });
      });
      return;
    }

    // Check if we're dragging over a column
    const overData = over.data.current;
    if (
      overData?.type === 'Column' &&
      activeService.columnId !== overData.columnId
    ) {
      setTimeout(() => {
        setAvailableServices((services) => {
          const activeIndex = services.findIndex((s) => s.id === activeId);
          const newServices = services.map((service) =>
            service.id === activeId
              ? { ...service, columnId: overData.columnId }
              : service,
          );
          return arrayMove(newServices, activeIndex, activeIndex);
        });
      });
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveService(null);

    if (!over) return;

    const serviceId = active.id;
    const overId = over.id;

    const service = availableServices.find((s) => s.id === serviceId);
    if (!service) return;

    // Check if we're dropping over a service
    const overService = availableServices.find((s) => s.id === overId);
    if (overService) {
      setTimeout(() => {
        setAvailableServices((services) => {
          const activeIndex = services.findIndex((s) => s.id === serviceId);
          const overIndex = services.findIndex((s) => s.id === overId);
          const activeService = services[activeIndex];
          const overService = services[overIndex];

          if (
            activeService &&
            overService &&
            activeService.columnId !== overService.columnId
          ) {
            // Moving between columns
            const newServices = services.map((service) =>
              service.id === serviceId
                ? { ...service, columnId: overService.columnId }
                : service,
            );
            return arrayMove(
              newServices,
              activeIndex,
              Math.max(overIndex - 1, 0),
            );
          }

          // Reordering within the same column
          return arrayMove(services, activeIndex, overIndex);
        });
      });
      return;
    }

    // Check if we're dropping over a column
    const overData = over.data.current;
    if (overData?.type === 'Column' && service.columnId !== overData.columnId) {
      setTimeout(() => {
        setAvailableServices((services) => {
          const activeIndex = services.findIndex((s) => s.id === serviceId);
          const newServices = services.map((service) =>
            service.id === serviceId
              ? { ...service, columnId: overData.columnId }
              : service,
          );
          return arrayMove(newServices, activeIndex, activeIndex);
        });
      });
    }
  };

  const unassignedServices = React.useMemo(
    () =>
      availableServices.filter((service) => service.columnId === 'unassigned'),
    [availableServices],
  );

  const routeServices = React.useMemo(
    () =>
      availableServices.filter((service) => service.columnId !== 'unassigned'),
    [availableServices],
  );

  // Update form's serviceIds when routeServices changes
  React.useEffect(() => {
    const serviceIds = routeServices.map((service) => String(service.id));
    updateForm({ serviceIds });
  }, [routeServices, updateForm]);

  return (
    <div className="@RouteServiceSelector flex min-h-[400px] flex-col rounded-md">
      {activeTab === 'services' ? (
        <DndContext
          sensors={sensors}
          onDragStart={isViewMode ? undefined : handleDragStart}
          onDragEnd={isViewMode ? undefined : handleDragEnd}
          onDragOver={isViewMode ? undefined : handleDragOver}
        >
          <div className="flex flex-1 gap-4">
            <ServiceListBox
              title={t.routesView.form.services.availableServices}
              placeholders={[
                isViewMode
                  ? t.routesView.form.services.viewModeMessage
                  : t.routesView.form.services.noAvailableServices,
              ]}
              services={isViewMode ? [] : unassignedServices}
              columnId="unassigned"
              isViewMode={isViewMode}
            />
            <ServiceListBox
              title={t.routesView.form.services.routeServices}
              placeholders={[
                t.routesView.form.services.noRouteServices,
                !isViewMode && t.routesView.form.services.dragFromLeft,
              ].filter(Boolean)}
              services={routeServices}
              columnId={form.id ?? 'route'}
              isViewMode={isViewMode}
            />
          </div>
          {createPortal(
            <DragOverlay>
              {activeService ? (
                <TaskCard task={activeService} isOverlay />
              ) : null}
            </DragOverlay>,
            document.body,
          )}
        </DndContext>
      ) : form.id ? (
        <RouteMapTab />
      ) : (
        <div className="flex flex-1 items-center justify-center text-foreground/40">
          {t.routesView.form.tabs.routeMapWip}
        </div>
      )}
    </div>
  );
};
